<template>
  <div class="data-paginator">
    <div class="paginator-right">
      <span class="items-label">Mostrado:</span>
      <select :value="itemsPerPage" @change="handleItemsPerPageChange" class="items-select">
        <option value="10">10</option>
        <option value="15">15</option>
        <option value="25">25</option>
        <option value="50">50</option>
      </select>
      <span class="page-range">{{ startItem }}-{{ endItem }} de {{ totalItems }}</span>
      <button
        @click="goToFirstPage"
        :disabled="currentPage === 1"
        class="nav-btn"
        title="Primera página"
      >
        <span class="nav-icon">K</span>
      </button>
      <button
        @click="goToPreviousPage"
        :disabled="currentPage === 1"
        class="nav-btn"
        title="Página anterior"
      >
        <span class="nav-icon">&lt;</span>
      </button>
      <button
        @click="goToNextPage"
        :disabled="currentPage >= totalPages"
        class="nav-btn"
        title="Página siguiente"
      >
        <span class="nav-icon">&gt;</span>
      </button>
      <button
        @click="goToLastPage"
        :disabled="currentPage >= totalPages"
        class="nav-btn"
        title="Última página"
      >
        <span class="nav-icon">&gt;|</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  totalItems: number
  currentPage: number
  itemsPerPage: number
}

interface Emits {
  (e: 'update:currentPage', page: number): void
  (e: 'update:itemsPerPage', items: number): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const totalPages = computed(() => Math.ceil(props.totalItems / props.itemsPerPage))
const startItem = computed(() => (props.currentPage - 1) * props.itemsPerPage + 1)
const endItem = computed(() => Math.min(props.currentPage * props.itemsPerPage, props.totalItems))

const goToFirstPage = () => {
  if (props.currentPage > 1) {
    emit('update:currentPage', 1)
  }
}

const goToPreviousPage = () => {
  if (props.currentPage > 1) {
    emit('update:currentPage', props.currentPage - 1)
  }
}

const goToNextPage = () => {
  if (props.currentPage < totalPages.value) {
    emit('update:currentPage', props.currentPage + 1)
  }
}

const goToLastPage = () => {
  if (props.currentPage < totalPages.value) {
    emit('update:currentPage', totalPages.value)
  }
}

const handleItemsPerPageChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  const newItemsPerPage = parseInt(target.value)
  emit('update:itemsPerPage', newItemsPerPage)
  // Reset to first page when changing items per page
  emit('update:currentPage', 1)
}
</script>

<style scoped>
.data-paginator {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem 0;
  margin-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

.items-label {
  font-size: 0.9rem;
  color: #666;
}

.items-select {
  padding: 0.25rem 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;
}

.page-range {
  font-size: 0.9rem;
  color: #666;
}

.paginator-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nav-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccc;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 28px;
  margin-left: 0.5rem;
}

.nav-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #999;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f5f5;
}

.nav-icon {
  font-size: 0.8rem;
  font-weight: bold;
  color: #333;
}

.nav-btn:disabled .nav-icon {
  color: #999;
}
</style>
